import { Box, Typography, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  VolumeOff as SilenceIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { Silence } from '../../../../../api/cansense/silenceApi';
import { getCardBorder, CARD_BORDER_RADIUS } from '../types';
import { SilenceCard } from './SilenceCard';
import { SilenceListSkeleton } from './SilenceListSkeleton';

export interface SilenceListProps {
  silences: Silence[];
  loading?: boolean;
  onCreateSilence?: () => void;
  onExtend?: (id: number) => void;
  onDelete?: (id: number) => void;
  showHeader?: boolean;
}

/**
 * SilenceList - Displays a list of active silences with management actions.
 *
 * Features:
 * - Header with count and "Create Silence" button
 * - List of SilenceCard components
 * - Empty state with helpful message
 * - Loading skeleton state
 */
export function SilenceList({
  silences,
  loading = false,
  onCreateSilence,
  onExtend,
  onDelete,
  showHeader = true,
}: SilenceListProps) {
  const theme = useTheme();

  // Show loading skeleton
  if (loading) {
    return <SilenceListSkeleton count={2} />;
  }

  return (
    <Box
      sx={{
        p: 2,
        border: getCardBorder(theme),
        borderRadius: CARD_BORDER_RADIUS,
      }}
    >
      {/* Header */}
      {showHeader && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SilenceIcon sx={{ color: 'text.secondary' }} />
            <Typography variant="h6" fontWeight={600}>
              Active Silences ({silences.length})
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={onCreateSilence}
            sx={{
              textTransform: 'none',
            }}
          >
            Create Silence
          </Button>
        </Box>
      )}

      {/* Silence Cards or Empty State */}
      {silences.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {silences.map((silence) => (
            <SilenceCard
              key={silence.id}
              silence={silence}
              onExtend={onExtend}
              onDelete={onDelete}
            />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            py: 6,
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          <SilenceIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            No active silences
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Silences temporarily mute alerts during maintenance windows
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={onCreateSilence}
            sx={{
              textTransform: 'none',
            }}
          >
            Create Your First Silence
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default SilenceList;
